import './Header.scss';
import React, {useContext, useEffect, useState} from 'react';
import block from 'bem-cn-lite';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {
    Breadcrumbs,
    Button,
    FirstDisplayedItemsCount,
    Icon,
    LastDisplayedItemsCount,
    Popup,
    Text,
    User
} from '@gravity-ui/uikit';
import {Box, CircleRuble, Handset, House} from '@gravity-ui/icons';
import LogoPng from '@assets/logo.svg';
import ContactCard, {hasContactData} from './components/ContactCard/ContactCard';
import UserCard from './components/UserCard/UserCard';
import useHoverCard from '@hooks/useHoverCardHeader';
import {AppContext} from 'app/context';
import {SearchInput} from './components/SearchInput/SearchInput';
import {usePopoverAuxControls, useSearchHeaderCompact} from '@hooks';
import {PopupClientsAddress} from './components/PopupClientsAddress/PopupClientsAddress';
import {BalanceElement} from './components/BalanceElement/BalanceElement';
import {isBasketLocation} from '@utils';
import {BasketButton} from '@features/search/components/SearchBar/components';
import {useMediaQuery} from 'react-responsive';
import {MobileMenu} from './components/MobileMenu/MobileMenu';
import {NavigationMobileMenu} from './components/NavigationMobileMenu/NavigationMobileMenu';
import {ContactsScreen} from './components/ContactsScreen/ContactsScreen';
import {MobileBalanceButton} from './components/MobileBalanceButton/MobileBalanceButton';
import {ModalQuit} from '@components/ModalQuit';
import {useMenuKeyboard} from '@hooks/keyboard';

const b = block('header');

export const Header: React.FC = () => {
    const {state, emit} = useContext(AppContext);
    const navigate = useNavigate();
    const location = useLocation();
    const {open, buttonRef, toggleOpen, closePopover} = usePopoverAuxControls();
    const isHeaderCompact = useSearchHeaderCompact();
    const {user} = state;

    const isSmallScreen = useMediaQuery({query: '(max-width: 1150px)'});

    const [showModalQuit, setShowModalQuit] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [isContactsOpen, setIsContactsOpen] = useState(false);
    const [breadcrumbItems, setBreadcrumbItems] = useState([
        {
            text: 'Главная',
            action: () => {
                emit('toggleBrandSearch', {active: false}); // Убираем поиск при клике на "Главная"
                navigate('/home');
            }
        }
    ]);

    const handleToggleMenu = () => {
        setIsMobileMenuOpen((prev) => !prev);
        if (isMobileMenuOpen) {
            setIsContactsOpen(false);
        }
    };

    const handleToggleMobileSearch = (isOpen: boolean) => {
        setIsMobileSearchOpen(isOpen);
    };

    const handleOpenContacts = () => {
        setIsContactsOpen(true);
    };

    const handleCloseContacts = () => {
        setIsContactsOpen(false);
    };

    useEffect(() => {
        if (isMobileMenuOpen && isSmallScreen) {
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollBarWidth}px`;

            return () => {
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            };
        }

        return undefined;
    }, [isMobileMenuOpen, isSmallScreen, setIsMobileMenuOpen]);

    useEffect(() => {
        if (
            location.pathname === '/basket' ||
            location.pathname === '/search' ||
            location.pathname === '/profile' ||
            location.pathname === '/orders' ||
            location.pathname === '/balance' ||
            location.pathname === '/balance/payment-schedule' ||
            location.pathname === '/balance/document-request' ||
            location.pathname === '/balance/document-flow'
        ) {
            if (state.isBrandSearchActive) {
                emit('toggleBrandSearch', {active: false});
            }
        }

        const newItems = [
            {
                text: 'Главная',
                action: () => {
                    emit('toggleBrandSearch', {active: false});
                    navigate('/home');
                }
            }
        ];

        // Добавляем элемент "Корзина", если находимся на странице корзины
        if (isBasketLocation()) {
            if (!newItems.some((item) => item.text === 'Корзина')) {
                newItems.push({
                    text: 'Корзина',
                    action: () => {
                        emit('toggleBrandSearch', {active: false});
                        navigate('/basket');
                    }
                });
            }
        }

        // Добавляем элемент "Профиль", если находимся на странице профиль
        if (location.pathname.includes('/profile')) {
            newItems.push({
                text: 'Профиль',
                action: () => {
                    emit('toggleBrandSearch', {active: false});
                    navigate('/profile');
                }
            });
        }

        if (location.pathname === '/search') {
            if (!newItems.some((item) => item.text === 'Поиск запчасти по коду')) {
                newItems.push({
                    text: 'Поиск запчасти по коду',
                    action: () => {
                        emit('toggleBrandSearch', {active: false});
                        navigate('/search');
                    }
                });
            }
        }

        if (location.pathname.startsWith('/orders')) {
            if (!newItems.some((item) => item.text === 'Заказы')) {
                newItems.push({
                    text: 'Заказы',
                    action: () => {
                        emit('toggleBrandSearch', {active: false});
                        navigate('/orders');
                    }
                });
            }

            if (location.pathname === '/orders/returns') {
                newItems.push({
                    text: 'Возвраты',
                    action: () => {
                        emit('toggleBrandSearch', {active: false});
                        navigate('/orders/returns');
                    }
                });
            }
        }

        if (location.pathname.startsWith('/balance')) {
            newItems.push({
                text: 'Баланс',
                action: () => {
                    emit('toggleBrandSearch', {active: false});
                    navigate('/balance');
                }
            });

            if (location.pathname === '/balance/payment-schedule') {
                newItems.push({
                    text: 'График платежей',
                    action: () => {
                        emit('toggleBrandSearch', {active: false});
                        navigate('/balance/payment-schedule');
                    }
                });
            }

            if (location.pathname === '/balance/document-request') {
                newItems.push({
                    text: 'Запрошенные документы',
                    action: () => {
                        emit('toggleBrandSearch', {active: false});
                        navigate('/balance/document-request');
                    }
                });
            }

            if (location.pathname === '/balance/document-flow') {
                newItems.push({
                    text: 'Документооборот',
                    action: () => {
                        emit('toggleBrandSearch', {active: false});
                        navigate('/balance/document-flow');
                    }
                });
            }
        }

        // Добавляем элемент "Уточнение запроса", если активен поиск бренда
        if (state.isBrandSearchActive) {
            newItems.push({
                text: 'Уточнение запроса',
                action: () => {}
            });
        }

        setBreadcrumbItems(newItems);
    }, [location, navigate, state.isBrandSearchActive]);

    // Хук для кнопки и ContactCard
    const {
        isVisible: isContactCardVisible,
        handleMouseEnter: handleMouseEnterContact,
        handleMouseLeave: handleMouseLeaveContact,
        handleFocus: handleFocusContact,
        handleBlur: handleBlurContact
    } = useHoverCard();

    // Хук для User и UserCard
    const {
        isVisible: isUserCardVisible,
        handleMouseEnter: handleMouseEnterUser,
        handleMouseLeave: handleMouseLeaveUser,
        handleFocus: handleFocusUser,
        handleBlur: handleBlurUser,
        hideUserCard
    } = useHoverCard();

    const handleLogoutRequest = () => {
        hideUserCard();
        setShowModalQuit(true);
    };

    const {highlightedIndex: profileMenuIndex} = useMenuKeyboard({
        isOpen: isUserCardVisible,
        items: ['profile', 'logout'] as const,
        onSelect: (item) => {
            if (item === 'profile') {
                navigate('/profile');
                hideUserCard();
            } else {
                handleLogoutRequest();
            }
        },
        onClose: hideUserCard
    });

    const handleGoToContacts = () => {
        navigate('/profile/contacts');
        handleMouseLeaveContact();
    };

    const hasAnyContacts =
        hasContactData(state.clientDetails?.mop) ||
        hasContactData(state.clientDetails?.mrk) ||
        hasContactData(state.clientDetails?.rop);

    // Получаем количество элементов в корзине (кроме недоступных)
    const basketItemCount =
        state.basket.items.filter((item) => item.status === 'actual' || item.status === 'stale')
            .length || 0;

    return (
        <>
            <header className={b({'search-active': isMobileSearchOpen, compact: isHeaderCompact})}>
                <div className={b('header-top')}>
                    <div className={b('left-section')}>
                        <Link to="/home">
                            <img src={LogoPng} className={b('header-logo')} alt="Авто-Транзит" />
                        </Link>

                        <Button
                            size="xl"
                            onClick={() => navigate('/home')}
                            className={b('home-button')}
                        >
                            <Icon data={House} width="20" height="20" />
                        </Button>

                        {hasAnyContacts ? (
                            <div
                                onMouseEnter={handleMouseEnterContact}
                                onMouseLeave={handleMouseLeaveContact}
                                onFocus={handleFocusContact}
                                onBlur={handleBlurContact}
                            >
                                <Button
                                    size="xl"
                                    className={b('contact-button')}
                                    onClick={handleGoToContacts}
                                    aria-haspopup="dialog"
                                    aria-expanded={isContactCardVisible}
                                    title="Контакты"
                                >
                                    <Icon data={Handset} width="20" height="20" />
                                </Button>
                                {isContactCardVisible && <ContactCard />}
                            </div>
                        ) : null}

                        {location.pathname !== '/home' && location.pathname !== '/search' && (
                            <SearchInput />
                        )}
                    </div>

                    <div className={b('right-section')}>
                        <Popup
                            anchorRef={buttonRef}
                            onClose={closePopover}
                            open={open}
                            placement="bottom-start"
                        >
                            <PopupClientsAddress open={open} onClose={closePopover} />
                        </Popup>

                        <BasketButton totalItems={basketItemCount} />

                        <Link to="/orders">
                            <Button size="xl" className={b('right-section-button')}>
                                <Box width="20" height="20" />

                                <Text>Заказы</Text>
                            </Button>
                        </Link>

                        <Link to="/balance">
                            <Button size="xl" className={b('right-section-button')}>
                                <CircleRuble width="20" height="20" />

                                <Text>Баланс</Text>
                            </Button>
                        </Link>

                        <div
                            onMouseEnter={handleMouseEnterUser}
                            onMouseLeave={handleMouseLeaveUser}
                            onFocus={handleFocusUser}
                            onBlur={handleBlurUser}
                        >
                            <div
                                className={b('avatar-trigger')}
                                role="button"
                                tabIndex={0}
                                aria-haspopup="dialog"
                                aria-expanded={isUserCardVisible}
                                aria-label="Профиль"
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleFocusUser();
                                    }
                                    if (event.key === 'Escape') {
                                        hideUserCard();
                                    }
                                }}
                            >
                                <User
                                    className={b('avatar')}
                                    avatar={{text: user?.displayName, theme: 'brand'}}
                                    name={user?.displayName}
                                    description={user?.login}
                                    size="l"
                                />
                            </div>

                            {isUserCardVisible && (
                                <UserCard
                                    onClose={hideUserCard}
                                    onLogoutRequest={handleLogoutRequest}
                                    highlightedIndex={profileMenuIndex}
                                />
                            )}
                        </div>

                        <ModalQuit
                            visibility={showModalQuit}
                            stateModal={setShowModalQuit}
                            positiveAction={() => emit('logout', {redirectTo: '/'})}
                        />
                    </div>
                </div>

                <div className={b('header-bottom')}>
                    <nav aria-label="breadcrumb">
                        <Breadcrumbs
                            items={breadcrumbItems}
                            firstDisplayedItemsCount={FirstDisplayedItemsCount.One}
                            lastDisplayedItemsCount={LastDisplayedItemsCount.One}
                        />
                    </nav>

                    <BalanceElement />
                </div>

                {isSmallScreen && (
                    <MobileMenu
                        isOpen={isMobileMenuOpen}
                        onToggle={handleToggleMenu}
                        onSearchToggle={handleToggleMobileSearch}
                    />
                )}
            </header>

            {isSmallScreen && (
                <div
                    className={b('mobile-breadcrumbs', {
                        sticky:
                            location.pathname.includes('/profile') ||
                            location.pathname.includes('/balance')
                    })}
                >
                    <Breadcrumbs
                        items={breadcrumbItems}
                        firstDisplayedItemsCount={FirstDisplayedItemsCount.One}
                        lastDisplayedItemsCount={LastDisplayedItemsCount.One}
                    />

                    <MobileBalanceButton />
                </div>
            )}

            {isSmallScreen && (
                <>
                    <NavigationMobileMenu
                        isOpen={isMobileMenuOpen}
                        onToggle={handleToggleMenu}
                        onOpenContacts={handleOpenContacts}
                    />
                    <ContactsScreen isOpen={isContactsOpen} onBack={handleCloseContacts} />
                </>
            )}
        </>
    );
};
